import React, { useCallback, useEffect, useState } from "react";
import FatsuNewsElement from "@assets/images/FatsuNewsElement.webp";
import { useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import useApiHook from "@src/hooks/useApiHook";
import useApiError from "@src/hooks/useApiError";
import NewsShowSkeleton from "@src/components/Skeleton/News/NewsShowSkeleton";
import NewsSlider from "./NewsSlider";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import ProductVideo from "../Products/ProductVideo";
import { getOptionBasedOnCondition } from "@src/utils/ConditionHelper";
import { getImageUrlWithExpiry } from "@src/utils/ImageExpirtyHelper";
import useReactQuery from "@src/hooks/useReactQuery";
import "@src/styles/NewsStyle.scss";
import type { INewsItem } from "@src/types/AppTypes";

const NewsShow: React.FC = () => {
  const { newsCategory } = useParams();
  const [news, setNews] = useState<INewsItem | null>(null);
  const newsApiEndpoint = useApiHook({ method: "get", url: "/news" });
  const params = useParams<{ id: string }>();
  const [newsList, setNewsList] = useState<INewsItem[]>([]);
  const setApiError = useApiError();

  const returnBgImages = useCallback((index: number) => {
    const remainder = index % 5;
    switch (remainder) {
      case 0:
        return "my-6 bg-radial-gradient p-6 border-2 border-black cursor-pointer duration-300 hover:rotate-0 hover:opacity-80 max-sm:rotate-0";
      case 1:
        return "my-6 bg-[url('@assets/images/news/pink-dot-bg.webp')] bg-no-repeat bg-cover bg-center p-6 border-2 border-black cursor-pointer duration-300 hover:rotate-0 hover:opacity-80 max-sm:rotate-0";
      case 2:
        return "my-6 bg-[url('@assets/images/news/blue-dot-bg.webp')] bg-no-repeat bg-cover bg-center p-6 border-2 border-black cursor-pointer duration-300 hover:rotate-0 hover:opacity-80 max-sm:rotate-0";
      case 3:
        return "my-6 bg-[url('@assets/images/news/pink-dot-bg.webp')] bg-no-repeat bg-cover bg-center p-6 border-2 border-black cursor-pointer duration-300 hover:rotate-0 hover:opacity-80 max-sm:rotate-0";
      case 4:
        return "my-6 bg-radial-gradient p-6 border-2 border-black cursor-pointer duration-300 hover:rotate-0 hover:opacity-80 max-sm:rotate-0";
      default:
        return "my-6 bg-radial-gradient p-6 border-2 border-black cursor-pointer duration-300 hover:rotate-0 hover:opacity-80 max-sm:rotate-0";
    }
  }, []);

  useEffect(() => {
    if (news?.id) {
      setNewsList((prev) => {
        return prev.filter((item) => item.id !== news?.id);
      });
    }
  }, [news]);

  const {
    data: newsData,
    isSuccess: newsIsSuccess,
    error: newsError,
  } = useReactQuery<{
    status: boolean;
    message: string;
    news: INewsItem[];
    latest_news?: INewsItem;
  }>({
    args: { queryKey: ["get-news"] },
    queryFn: () => newsApiEndpoint(null, "", null),
  });

  useEffect(() => {
    if (newsIsSuccess && newsData) {
      setNewsList(
        newsData.data.news.filter((v) => {
          return v.news_type.toLowerCase().replace(/ /g, "-") === newsCategory;
        })
      );
    } else if (newsError) {
      setApiError(newsError);
    }
  }, [newsIsSuccess, newsData, newsError, setApiError, newsCategory]);

  const {
    data: singleNewsData,
    isSuccess: singleNewsIsSuccess,
    error: singleNewsError,
    isLoading: singleNewsIsLoading,
  } = useReactQuery<{
    status: boolean;
    message: string;
    news: INewsItem;
  }>({
    args: { queryKey: ["get-single-news", params.id] },
    queryFn: () => newsApiEndpoint(null, `/${params.id}`, null),
  });

  useEffect(() => {
    if (singleNewsIsSuccess && singleNewsData) {
      setNews(singleNewsData.data.news);
    } else if (singleNewsError) {
      setApiError(singleNewsError);
    }
  }, [singleNewsIsSuccess, singleNewsData, singleNewsError, setApiError]);

  return (
    <main>
      <section className="py-12 min-h-[50vh] bg-[url('@assets/images/NewsBackBG.webp')] bg-full">
        <div className="container mx-auto">
          <div className="text-white relative">
            {singleNewsIsLoading ? (
              <NewsShowSkeleton />
            ) : news ? (
              <div className="grid grid-cols-2 max-md:grid-cols-1">
                <div className="p-6 backdrop-blur supports-backdrop-blur:bg-white/60">
                  <h2 className="text-[#91DBE7] text-6xl max-xl:text-4xl max-md:text-2xl text-start mb-10">
                    {news.title}
                  </h2>
                  <div className="flex flex-col gap-8 font-mikado-regular font-bold">
                    <p className="text-2xl max-sm:text-xl text-primary break-words">
                      {getOptionBasedOnCondition(
                        news?.short_desc === null || news?.short_desc === "",
                        "",
                        news.short_desc
                      )}
                    </p>
                  </div>
                </div>
                <div className="ms-16 max-md:mx-6 relative z-[2]">
                  <img
                    src={FatsuNewsElement}
                    className="absolute right-20 top-10 -z-[1] bg-contain w-1/3"
                    alt=""
                  />
                  <div>
                    <div className="bg-radial-gradient-light h-[560px] p-4 rounded-[20%] max-sm:rounded-xl max-w-[490px] first-news-slide news-main-page">
                      <Carousel
                        showStatus={false}
                        showThumbs={false}
                        infiniteLoop={true}
                      >
                        {news?.thumbnail && (
                          <div className="relative h-[525px] rounded-[10%] my-auto w-full overflow-hidden">
                            <img
                              className="h-full w-full object-contain relative z-10"
                              src={getImageUrlWithExpiry(news.thumbnail)}
                              alt="product img"
                            />
                            <img
                              className="h-full w-full object-cover absolute top-0 left-0 blur-sm"
                              src={getImageUrlWithExpiry(news.thumbnail)}
                              alt="product img"
                            />
                          </div>
                        )}
                        {news?.images &&
                          news.images.map((item) => {
                            return (
                              <div
                                className="relative h-[525px] rounded-[10%] my-auto w-full overflow-hidden"
                                key={item}
                              >
                                <img
                                  src={getImageUrlWithExpiry(item)}
                                  className="h-full w-full object-contain relative z-10"
                                  alt="news-cover-image"
                                />
                                <img
                                  className="h-full w-full object-cover absolute top-0 left-0 blur-sm"
                                  src={getImageUrlWithExpiry(item)}
                                  alt="news-cover-image"
                                />
                              </div>
                            );
                          })}
                        {[news?.video]
                          .filter((i) => !!i)
                          .map((item) => {
                            return <ProductVideo vidUrl={item} />;
                          })}
                      </Carousel>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-2xl max-md:text-lg text-primary text-center flex justify-center items-center min-h-[330px]">
                Fatsu news coming soon!
              </div>
            )}
          </div>
        </div>
      </section>
      <div className="py-12 news-slider radial-gradient-pink-bg">
        <div className="container mx-auto px-4">
          {news && (
            <h3
              className="text-2xl text-white break-words font-mikado-regular text-center font-semibold"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  getOptionBasedOnCondition(
                    news?.description === null || news?.description === "",
                    "No news description",
                    news.description
                  )
                ),
              }}
            ></h3>
          )}
        </div>
      </div>
      {newsList.length > 0 && (
        <div className="py-12 news-slider blue-bg-dots">
          <div className="container mx-auto px-4">
            <h2 className="text-[#91DBE7] text-8xl max-md:text-6xl max-md:mb-10 mb-10 text-start">
              Read more news
            </h2>
          </div>
          <NewsSlider
            showNews={true}
            newsList={newsList.filter((item) => item.id !== news?.id)}
            inHouseList={newsList.filter((item) => item.id !== news?.id)}
            returnBgImages={returnBgImages}
          />
        </div>
      )}
    </main>
  );
};

export default NewsShow;
